import { Check, Copy, Download, ExternalLink, QrCode, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/lib/notify';

import { Button } from '@/components/ui/button';
import type { CustomerBillingInvoice } from '@/types/customer-billing';

export function BillingChargeActions({
  invoice,
  onSecondCopy,
  onGeneratePix,
  onSync,
  onViewQr,
  loading,
  providerManaged,
}: {
  invoice: CustomerBillingInvoice;
  onSecondCopy?: () => void;
  onGeneratePix?: () => void;
  onSync?: () => void;
  onViewQr?: () => void;
  loading?: boolean;
  providerManaged?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const charge = invoice.asaas ?? invoice.bolix ?? invoice.pix;
  const isInfinitePay = invoice.provider === 'INFINITEPAY';
  const pix = charge?.pixCopyPaste;
  const payUrl =
    invoice.asaas?.checkoutUrl ??
    invoice.asaas?.bankSlipUrl ??
    invoice.checkout?.checkoutUrl ??
    invoice.bolix?.paymentUrl ??
    invoice.bolix?.billetUrl ??
    invoice.paymentUrl;
  const pdfUrl = invoice.asaas?.bankSlipUrl ?? invoice.bolix?.billetPdfUrl;

  const openPaymentLink = () => {
    if (payUrl) window.location.assign(payUrl);
  };

  const copyPix = async () => {
    if (!pix) return;
    await navigator.clipboard.writeText(pix);
    setCopied(true);
    toast.success('Código PIX copiado.');
    window.setTimeout(() => setCopied(false), 1800);
  };

  if (providerManaged && !charge) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-[rgb(var(--muted-foreground))]">
          Cobrança sendo preparada pela recorrência Asaas.
        </span>
        {onSync ? (
          <Button type="button" variant="outline" disabled={loading} onClick={onSync}>
            <RefreshCw className={loading ? 'animate-spin' : ''} /> Atualizar pagamento
          </Button>
        ) : null}
      </div>
    );
  }

  if (
    isInfinitePay ||
    (invoice.provider === 'ASAAS' && (!charge || invoice.paymentMethod === 'CREDIT_CARD'))
  ) {
    return (
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          disabled={loading || (!payUrl && !onGeneratePix)}
          onClick={payUrl ? openPaymentLink : onGeneratePix}
        >
          <ExternalLink />
          {payUrl ? 'Abrir link de pagamento' : 'Gerar link de pagamento'}
        </Button>
        {onSync ? (
          <Button type="button" variant="ghost" disabled={loading} onClick={onSync}>
            <RefreshCw className={loading ? 'animate-spin' : ''} /> Atualizar pagamento
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {!pix && onGeneratePix ? (
        <Button type="button" onClick={onGeneratePix} disabled={loading}>
          <QrCode /> Gerar PIX
        </Button>
      ) : null}
      {pix ? (
        <Button type="button" onClick={() => void copyPix()}>
          {copied ? <Check /> : <Copy />}
          {copied ? 'PIX copiado' : 'Copiar PIX'}
        </Button>
      ) : null}
      {payUrl && !pix ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => window.open(payUrl, '_blank', 'noopener,noreferrer')}
        >
          <ExternalLink /> Pagar agora
        </Button>
      ) : null}
      {charge?.qrCodeImage && onViewQr ? (
        <Button type="button" variant="outline" onClick={onViewQr}>
          <QrCode /> Ver QR Code
        </Button>
      ) : null}
      {pdfUrl ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
        >
          <Download /> Baixar boleto (PDF)
        </Button>
      ) : null}
      {onSecondCopy ? (
        <Button type="button" variant="ghost" disabled={loading} onClick={onSecondCopy}>
          <RefreshCw className={loading ? 'animate-spin' : ''} />
          Segunda via
        </Button>
      ) : null}
      {onSync ? (
        <Button type="button" variant="ghost" disabled={loading} onClick={onSync}>
          <RefreshCw className={loading ? 'animate-spin' : ''} /> Atualizar pagamento
        </Button>
      ) : null}
    </div>
  );
}
